import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { useSchool } from '../contexts/SchoolContext';
import { supabase, escapeFilterValue } from '../lib/supabase';
import {
  APPLICATION_COLUMNS, STATUSES, statusColor, statusLabel, fetchSensitive, toLocalInput, fromLocalInput
} from '../lib/applications';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaSearch, FaDownload, FaEye, FaTimes,
  FaEdit, FaSave, FaStickyNote, FaHistory, FaFileAlt, FaPrint
} from 'react-icons/fa';
import './ApplicationsManagement.css';

const SEARCH_COLUMNS = [
  'reference_number', 'student_first_name', 'student_last_name', 'parent_first_name', 'parent_last_name', 'email'
];
const EXPORT_PAGE_SIZE = 1000;
const EMPTY_FILTERS = {
  search: '', status: '', grade: '', academicYear: '', duplicatesOnly: false, sort: 'newest', startDate: '', endDate: ''
};

const ApplicationsManagement = () => {
  const { isViewer, user } = useAuth();
  const { grades, gradeLabel } = useSchool();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalTab, setModalTab] = useState('details');
  const [years, setYears] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [bulk, setBulk] = useState({ status: '', notes: '' });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [notice, setNotice] = useState({ type: '', text: '' });
  const [includeSensitive, setIncludeSensitive] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [interviewInput, setInterviewInput] = useState('');

  // Filters
  const location = useLocation();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS,
    status: searchParams.get('status') || '',
    startDate: searchParams.get('startDate') || '',
    duplicatesOnly: searchParams.get('duplicates') === '1',
    endDate: searchParams.get('endDate') || ''
  });

  // Pagination
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  // Status update
  const [statusUpdate, setStatusUpdate] = useState({ status: '', notes: '' });

  // New note
  const [newNote, setNewNote] = useState({ note: '', is_internal: false });

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  useEffect(() => {
    supabase.rpc('application_academic_years').then(({ data, error }) => {
      if (!error && Array.isArray(data)) setYears(data);
    });
  }, []);

  // /admin/applications/:id opens that application; the URL is the source of truth.
  useEffect(() => {
    if (routeId) {
      fetchApplicationDetails(routeId);
    } else {
      setShowModal(false);
      setSelectedApp(null);
      setModalTab('details');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // Dates compare on the UTC calendar day, as the old admin API did.
  const applyFilters = (query) => {
    if (filters.status) query = query.eq('status', filters.status);
    if (filters.grade) query = query.eq('grade', filters.grade);
    if (filters.academicYear) query = query.eq('academic_year', filters.academicYear);
    if (filters.duplicatesOnly) query = query.not('duplicate_of', 'is', null);
    const term = escapeFilterValue(filters.search || '');
    if (term) {
      query = query.or(SEARCH_COLUMNS.map((column) => `${column}.ilike.*${term}*`).join(','));
    }
    if (filters.startDate) query = query.gte('created_at', `${filters.startDate}T00:00:00Z`);
    if (filters.endDate) {
      const dayAfter = new Date(`${filters.endDate}T00:00:00Z`);
      dayAfter.setUTCDate(dayAfter.getUTCDate() + 1);
      query = query.lt('created_at', dayAfter.toISOString());
    }
    return query.order('created_at', { ascending: filters.sort === 'oldest' });
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const from = (pagination.page - 1) * pagination.limit;
      const { data, count, error } = await applyFilters(
        supabase.from('admissions').select(APPLICATION_COLUMNS, { count: 'exact' })
      ).range(from, from + pagination.limit - 1);
      if (error) throw error;

      setApplications(data ?? []);
      setSelected(new Set());
      setPagination(prev => ({
        ...prev,
        total: count ?? 0,
        totalPages: Math.ceil((count ?? 0) / prev.limit)
      }));
    } catch (error) {
      console.error('Error fetching applications:', error);
      setLoadError('Failed to load applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicationDetails = async (id) => {
    try {
      const [application, history, notes, laterCopies] = await Promise.all([
        supabase.from('admissions').select(APPLICATION_COLUMNS).eq('id', id).single(),
        supabase
          .from('status_history')
          .select('*, changed_by_user:admin_users(full_name)')
          .eq('application_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('admin_notes')
          .select('*, admin:admin_users(full_name)')
          .eq('application_id', id)
          .order('created_at', { ascending: false }),
        supabase.from('admissions').select('id, reference_number').eq('duplicate_of', id)
      ]);
      const failure = application.error || history.error || notes.error;
      if (failure) throw failure;

      const app = application.data;
      let duplicateOf = null;
      if (app.duplicate_of) {
        const { data } = await supabase.from('admissions').select('id, reference_number').eq('id', app.duplicate_of).single();
        duplicateOf = data ?? { id: app.duplicate_of, reference_number: `#${app.duplicate_of}` };
      }
      // Medical and special-needs answers: admins only, through the database function.
      let sensitive = null;
      if (!isViewer) {
        try {
          sensitive = (await fetchSensitive([app.id], 'view'))[app.id] ?? { medical_conditions: null, special_needs: null };
        } catch (e) {
          console.error('Error loading medical information:', e);
          sensitive = { error: true };
        }
      }

      setSelectedApp({
        application: app,
        sensitive,
        duplicateOf,
        laterCopies: laterCopies.data ?? [],
        statusHistory: history.data.map(h => ({ ...h, changed_by_name: h.changed_by_user?.full_name })),
        notes: notes.data.map(n => ({ ...n, admin_name: n.admin?.full_name }))
      });
      setStatusUpdate({ status: app.status, notes: '' });
      setInterviewInput(toLocalInput(app.interview_at));
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching application details:', error);
      if (routeId) {
        alert('Application not found');
        navigate('/admin/applications', { replace: true });
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleUpdateStatus = async () => {
    if (isViewer) {
      alert('Viewers cannot update application status');
      return;
    }

    try {
      const { error } = await supabase.rpc('update_application_status', {
        p_id: selectedApp.application.id,
        p_status: statusUpdate.status,
        p_notes: statusUpdate.notes
      });
      if (error) throw error;

      alert('Status updated successfully!');
      fetchApplicationDetails(selectedApp.application.id);
      fetchApplications();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleSaveInterview = async (clear = false) => {
    const value = clear ? null : fromLocalInput(interviewInput);
    const { error } = await supabase.rpc('set_interview_date', { p_id: selectedApp.application.id, p_at: value });
    if (error) {
      console.error('Error saving interview date:', error);
      alert('Failed to save the interview date');
      return;
    }
    fetchApplicationDetails(selectedApp.application.id);
  };

  const handleAddNote = async () => {
    if (!newNote.note.trim()) {
      alert('Please enter a note');
      return;
    }

    try {
      const { error } = await supabase.from('admin_notes').insert({
        application_id: selectedApp.application.id,
        admin_id: user.id,
        note: newNote.note,
        is_internal: newNote.is_internal
      });
      if (error) throw error;

      setNewNote({ note: '', is_internal: false });
      alert('Note added successfully!');
      fetchApplicationDetails(selectedApp.application.id);
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note');
    }
  };

  // ------------------------------------------------------------------------------------------- bulk update
  const toggleSelected = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allOnPageSelected = applications.length > 0 && applications.every((a) => selected.has(a.id));
  const toggleAllOnPage = () =>
    setSelected(allOnPageSelected ? new Set() : new Set(applications.map((a) => a.id)));

  const handleBulkUpdate = async () => {
    if (!bulk.status || selected.size === 0) return;
    const count = selected.size;
    if (!window.confirm(`Change the status of ${count} application${count === 1 ? '' : 's'} to "${statusLabel(bulk.status)}"?`)) {
      return;
    }
    setBulkBusy(true);
    setNotice({ type: '', text: '' });
    const { data, error } = await supabase.rpc('bulk_update_application_status', {
      p_ids: Array.from(selected),
      p_status: bulk.status,
      p_notes: bulk.notes || null
    });
    setBulkBusy(false);
    if (error) {
      console.error('Bulk update failed:', error);
      setNotice({ type: 'error', text: 'The status change failed and nothing was changed. Please try again.' });
      return;
    }
    setNotice({ type: 'success', text: `Updated ${data ?? count} application${(data ?? count) === 1 ? '' : 's'}.` });
    setBulk({ status: '', notes: '' });
    fetchApplications();
  };

  // ------------------------------------------------------------------------------------------------ export
  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = [];
      for (let from = 0; ; from += EXPORT_PAGE_SIZE) {
        const { data, error } = await applyFilters(supabase.from('admissions').select(APPLICATION_COLUMNS))
          .range(from, from + EXPORT_PAGE_SIZE - 1);
        if (error) throw error;
        rows.push(...data);
        if (data.length < EXPORT_PAGE_SIZE) break;
      }

      const withSensitive = includeSensitive && !isViewer;
      const sensitive = withSensitive ? await fetchSensitive(rows.map((r) => r.id), 'export') : {};

      // Every export is recorded (who, how many, which filters). No log, no file.
      const { error: logError } = await supabase.rpc('log_application_export', {
        p_count: rows.length,
        p_filters: Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '' && v !== false)),
        p_include_sensitive: withSensitive
      });
      if (logError) throw logError;

      const ExcelJS = (await import('exceljs/dist/exceljs.min.js')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Applications');
      worksheet.columns = [
        { header: 'Reference', key: 'reference_number', width: 20 },
        { header: 'Academic Year', key: 'academic_year', width: 14 },
        { header: 'Status', key: 'status', width: 15 },
        { header: 'Possible Duplicate', key: 'possible_duplicate', width: 12 },
        { header: 'Interview/Test', key: 'interview_at', width: 20 },
        { header: 'Student First Name', key: 'student_first_name', width: 20 },
        { header: 'Student Last Name', key: 'student_last_name', width: 20 },
        { header: 'Date of Birth', key: 'date_of_birth', width: 15 },
        { header: 'Gender', key: 'gender', width: 10 },
        { header: 'Grade', key: 'grade', width: 15 },
        { header: 'Parent First Name', key: 'parent_first_name', width: 20 },
        { header: 'Parent Last Name', key: 'parent_last_name', width: 20 },
        { header: 'Email', key: 'email', width: 30 },
        { header: 'Phone', key: 'phone', width: 15 },
        { header: 'Address', key: 'address', width: 30 },
        { header: 'City', key: 'city', width: 15 },
        { header: 'State', key: 'state', width: 15 },
        { header: 'Zip Code', key: 'zip_code', width: 10 },
        { header: 'Emergency Contact', key: 'emergency_contact_name', width: 20 },
        { header: 'Emergency Phone', key: 'emergency_contact_phone', width: 15 },
        { header: 'Emergency Relation', key: 'emergency_contact_relation', width: 15 },
        { header: 'Previous School', key: 'previous_school', width: 25 },
        ...(withSensitive ? [
          { header: 'Medical Conditions', key: 'medical_conditions', width: 30 },
          { header: 'Special Needs', key: 'special_needs', width: 30 }
        ] : []),
        { header: 'How Did You Hear', key: 'how_did_you_hear', width: 20 },
        { header: 'Comments', key: 'comments', width: 40 },
        { header: 'Submitted Date', key: 'created_at', width: 20 },
        { header: 'Source', key: 'utm_source', width: 18 },
        { header: 'Medium', key: 'utm_medium', width: 14 },
        { header: 'Campaign', key: 'utm_campaign', width: 18 },
        { header: 'Referrer', key: 'referrer', width: 30 },
        { header: 'Privacy Policy Accepted', key: 'consent_at', width: 22 }
      ];
      worksheet.addRows(rows.map((row) => ({
        ...row,
        ...(withSensitive ? {
          medical_conditions: sensitive[row.id]?.medical_conditions ?? '',
          special_needs: sensitive[row.id]?.special_needs ?? ''
        } : {}),
        grade: gradeLabel(row.grade),
        status: statusLabel(row.status),
        possible_duplicate: row.duplicate_of ? 'Yes' : ''
      })));
      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF2ECC71' }
      };

      const buffer = await workbook.xlsx.writeBuffer();
      const url = URL.createObjectURL(new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `applications_${Date.now()}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Failed to export applications');
    } finally {
      setExporting(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedApp(null);
    setModalTab('details');
    setNewNote({ note: '', is_internal: false });
    if (routeId) navigate('/admin/applications');
  };

  const app = selectedApp?.application;
  const columnCount = isViewer ? 9 : 10;

  return (
    <AdminLayout>
      <div className="applications-management">
      <div className="page-header">
        <div>
          <h1>Applications Management</h1>
          <p>View and manage all admission applications</p>
        </div>
        <div className="export-controls">
          {!isViewer && (
            <label className="include-sensitive">
              <input
                type="checkbox"
                checked={includeSensitive}
                onChange={(e) => setIncludeSensitive(e.target.checked)}
              />
              Include medical and special-needs answers
            </label>
          )}
          <button className="btn-export" onClick={handleExport} disabled={exporting}>
            <FaDownload /> {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <FaSearch />
            <input
              type="text"
              name="search"
              aria-label="Search applications"
              placeholder="Search by reference, name, email..."
              value={filters.search}
              onChange={handleFilterChange}
            />
          </div>

          <select name="status" aria-label="Status" value={filters.status} onChange={handleFilterChange}>
            <option value="">All Status</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <select name="grade" aria-label="Grade" value={filters.grade} onChange={handleFilterChange}>
            <option value="">All Grades</option>
            {(grades ?? []).map((g) => (
              <option key={g.key} value={g.key}>{g.label}</option>
            ))}
          </select>

          <select name="academicYear" aria-label="Academic year" value={filters.academicYear} onChange={handleFilterChange}>
            <option value="">All Academic Years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>

          <select name="sort" aria-label="Sort by date" value={filters.sort} onChange={handleFilterChange}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>

          <input
            type="date"
            name="startDate"
            aria-label="Submitted from"
            value={filters.startDate}
            onChange={handleFilterChange}
          />

          <input
            type="date"
            name="endDate"
            aria-label="Submitted until"
            value={filters.endDate}
            onChange={handleFilterChange}
          />

          <label className="filter-checkbox">
            <input
              type="checkbox"
              name="duplicatesOnly"
              checked={filters.duplicatesOnly}
              onChange={handleFilterChange}
            />
            Possible duplicates only
          </label>

          <button className="btn-clear-filters" onClick={clearFilters}>
            <FaTimes /> Clear
          </button>
        </div>
      </div>

      {notice.text && <div className={`alert alert-${notice.type}`} role="status">{notice.text}</div>}

      {/* Bulk status update */}
      {!isViewer && selected.size > 0 && (
        <div className="bulk-bar" role="region" aria-label="Bulk status update">
          <strong>{selected.size} selected</strong>
          <select
            aria-label="New status for selected applications"
            value={bulk.status}
            onChange={(e) => setBulk({ ...bulk, status: e.target.value })}
          >
            <option value="">Choose a status...</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <input
            type="text"
            aria-label="Notes for the status change"
            placeholder="Notes (optional)"
            value={bulk.notes}
            maxLength={1000}
            onChange={(e) => setBulk({ ...bulk, notes: e.target.value })}
          />
          <button className="btn-save" onClick={handleBulkUpdate} disabled={!bulk.status || bulkBusy}>
            {bulkBusy ? 'Updating...' : 'Apply'}
          </button>
          <button className="btn-clear-filters" onClick={() => setSelected(new Set())}>Clear selection</button>
        </div>
      )}

      {/* Applications Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading applications...</p>
          </div>
        ) : loadError ? (
          <div className="error-banner" role="alert">{loadError}</div>
        ) : (
          <>
            <table className="applications-table">
              <thead>
                <tr>
                  {!isViewer && (
                    <th>
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        checked={allOnPageSelected}
                        onChange={toggleAllOnPage}
                        disabled={applications.length === 0}
                      />
                    </th>
                  )}
                  <th>Reference</th>
                  <th>Student Name</th>
                  <th>Grade</th>
                  <th>Parent Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className="no-data">
                      No applications found
                    </td>
                  </tr>
                ) : (
                  applications.map((row) => (
                    <tr key={row.id}>
                      {!isViewer && (
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${row.reference_number}`}
                            checked={selected.has(row.id)}
                            onChange={() => toggleSelected(row.id)}
                          />
                        </td>
                      )}
                      <td>
                        {row.reference_number}
                        {row.duplicate_of && <span className="duplicate-badge" title="Same child, birth date and parent email as an earlier application this academic year">Possible duplicate</span>}
                      </td>
                      <td>{row.student_first_name} {row.student_last_name}</td>
                      <td>{gradeLabel(row.grade)}</td>
                      <td>{row.parent_first_name} {row.parent_last_name}</td>
                      <td>{row.email}</td>
                      <td>{row.phone}</td>
                      <td>
                        <span className="status-badge" style={{ background: statusColor(row.status) }}>
                          {statusLabel(row.status)}
                        </span>
                      </td>
                      <td>{new Date(row.created_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="btn-view"
                          onClick={() => navigate(`/admin/applications/${row.id}`)}
                        >
                          <FaEye /> View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
                <span>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Application Details Modal */}
      <AnimatePresence>
        {showModal && selectedApp && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="modal-content large"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Application #{app.id}</h2>
                <div className="modal-header-actions">
                  <Link
                    className="btn-view"
                    to={`/admin/applications/${app.id}/print`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaPrint /> Print view
                  </Link>
                  <button className="btn-close" onClick={closeModal} aria-label="Close">
                    <FaTimes />
                  </button>
                </div>
              </div>

              <div className="modal-tabs">
                <button className={modalTab === 'details' ? 'active' : ''} onClick={() => setModalTab('details')}>
                  <FaFileAlt /> Details
                </button>
                <button className={modalTab === 'status' ? 'active' : ''} onClick={() => setModalTab('status')}>
                  <FaEdit /> Update Status
                </button>
                <button className={modalTab === 'notes' ? 'active' : ''} onClick={() => setModalTab('notes')}>
                  <FaStickyNote /> Notes ({selectedApp.notes.length})
                </button>
                <button className={modalTab === 'history' ? 'active' : ''} onClick={() => setModalTab('history')}>
                  <FaHistory /> History ({selectedApp.statusHistory.length})
                </button>
              </div>

              <div className="modal-body">
                {modalTab === 'details' && (
                  <div className="details-view">
                    {(selectedApp.duplicateOf || selectedApp.laterCopies.length > 0) && (
                      <div className="duplicate-warning" role="note">
                        {selectedApp.duplicateOf && (
                          <p>
                            <strong>Possible duplicate</strong> of{' '}
                            <Link to={`/admin/applications/${selectedApp.duplicateOf.id}`}>{selectedApp.duplicateOf.reference_number}</Link>
                            {' '}(same child, date of birth and parent email this academic year). Merge or close the
                            extra record if it is a repeat; siblings and corrected submissions are legitimate.
                          </p>
                        )}
                        {selectedApp.laterCopies.length > 0 && (
                          <p>
                            <strong>Later possible duplicate{selectedApp.laterCopies.length > 1 ? 's' : ''}:</strong>{' '}
                            {selectedApp.laterCopies.map((d, i) => (
                              <React.Fragment key={d.id}>
                                {i > 0 && ', '}
                                <Link to={`/admin/applications/${d.id}`}>{d.reference_number}</Link>
                              </React.Fragment>
                            ))}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="detail-section">
                      <h3>Application</h3>
                      <div className="detail-grid">
                        <div><strong>Reference:</strong> <span data-testid="detail-reference">{app.reference_number}</span></div>
                        <div><strong>Status:</strong> {statusLabel(app.status)}</div>
                        <div><strong>Submitted:</strong> {new Date(app.created_at).toLocaleString()}</div>
                        {app.academic_year && (
                          <div><strong>Academic year:</strong> {app.academic_year}</div>
                        )}
                        {app.consent_at && (
                          <div>
                            <strong>Privacy policy accepted:</strong>{' '}
                            {new Date(app.consent_at).toLocaleString()}
                            {' '}(version {app.consent_policy_version})
                          </div>
                        )}
                        {(app.utm_source || app.utm_medium || app.utm_campaign || app.referrer) && (
                          <div>
                            <strong>Source:</strong>{' '}
                            {[app.utm_source, app.utm_medium, app.utm_campaign].filter(Boolean).join(' / ')}
                            {app.referrer && ` (from ${app.referrer})`}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="detail-section">
                      <h3>Interview / Test</h3>
                      {isViewer ? (
                        <p data-testid="interview-date">
                          {app.interview_at ? new Date(app.interview_at).toLocaleString() : 'Not scheduled'}
                        </p>
                      ) : (
                        <div className="interview-editor">
                          <label htmlFor="interview-at">Date and time</label>
                          <input
                            id="interview-at"
                            type="datetime-local"
                            value={interviewInput}
                            onChange={(e) => setInterviewInput(e.target.value)}
                          />
                          <button
                            className="btn-save"
                            onClick={() => handleSaveInterview(false)}
                            disabled={!interviewInput || interviewInput === toLocalInput(app.interview_at)}
                          >
                            Save
                          </button>
                          {app.interview_at && (
                            <button className="btn-clear-filters" onClick={() => handleSaveInterview(true)}>Clear date</button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="detail-section">
                      <h3>Student Information</h3>
                      <div className="detail-grid">
                        <div><strong>Name:</strong> {app.student_first_name} {app.student_last_name}</div>
                        <div><strong>Date of Birth:</strong> {new Date(app.date_of_birth).toLocaleDateString()}</div>
                        <div><strong>Gender:</strong> {app.gender}</div>
                        <div><strong>Grade:</strong> {gradeLabel(app.grade)}</div>
                      </div>
                    </div>

                    <div className="detail-section">
                      <h3>Parent/Guardian Information</h3>
                      <div className="detail-grid">
                        <div><strong>Name:</strong> {app.parent_first_name} {app.parent_last_name}</div>
                        <div><strong>Email:</strong> {app.email}</div>
                        <div><strong>Phone:</strong> {app.phone}</div>
                        <div><strong>Address:</strong> {app.address}, {app.city}, {app.state} {app.zip_code}</div>
                      </div>
                    </div>

                    <div className="detail-section">
                      <h3>Emergency Contact</h3>
                      <div className="detail-grid">
                        <div><strong>Name:</strong> {app.emergency_contact_name}</div>
                        <div><strong>Phone:</strong> {app.emergency_contact_phone}</div>
                        <div><strong>Relationship:</strong> {app.emergency_contact_relation}</div>
                      </div>
                    </div>

                    <div className="detail-section">
                      <h3>Additional Information</h3>
                      <div className="detail-grid">
                        <div><strong>Previous School:</strong> {app.previous_school || '—'}</div>
                        <div><strong>How Did You Hear:</strong> {app.how_did_you_hear || '—'}</div>
                        {app.comments && <div className="full-width"><strong>Comments:</strong> {app.comments}</div>}
                      </div>
                    </div>

                    <div className="detail-section sensitive-section">
                      <h3>Medical and Special Needs</h3>
                      {isViewer ? (
                        <p className="help-text">Medical and special-needs answers are visible to admin staff only.</p>
                      ) : selectedApp.sensitive?.error ? (
                        <p className="help-text">This information could not be loaded.</p>
                      ) : (
                        <div className="detail-grid">
                          <div className="full-width"><strong>Medical Conditions:</strong> {selectedApp.sensitive?.medical_conditions || 'None given'}</div>
                          <div className="full-width"><strong>Special Needs:</strong> {selectedApp.sensitive?.special_needs || 'None given'}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {modalTab === 'status' && (
                  <div className="status-update-view">
                    <div className="current-status">
                      <span>Current Status: </span>
                      <span className="status-badge" style={{ background: statusColor(app.status) }}>
                        {statusLabel(app.status)}
                      </span>
                    </div>

                    <div className="form-group">
                      <label htmlFor="status-new">New Status</label>
                      <select
                        id="status-new"
                        value={statusUpdate.status}
                        onChange={(e) => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
                        disabled={isViewer}
                      >
                        {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="status-notes">Notes (Optional)</label>
                      <textarea
                        id="status-notes"
                        value={statusUpdate.notes}
                        onChange={(e) => setStatusUpdate({ ...statusUpdate, notes: e.target.value })}
                        placeholder="Add notes about this status change..."
                        rows="4"
                        disabled={isViewer}
                      />
                    </div>

                    <button
                      className="btn-save"
                      onClick={handleUpdateStatus}
                      disabled={isViewer || statusUpdate.status === app.status}
                    >
                      <FaSave /> Update Status
                    </button>
                  </div>
                )}

                {modalTab === 'notes' && (
                  <div className="notes-view">
                    <div className="add-note-section">
                      <h3>Add New Note</h3>
                      <textarea
                        aria-label="New note"
                        value={newNote.note}
                        onChange={(e) => setNewNote({ ...newNote, note: e.target.value })}
                        placeholder="Enter your note here..."
                        rows="3"
                      />
                      <div className="note-options">
                        <label>
                          <input
                            type="checkbox"
                            checked={newNote.is_internal}
                            onChange={(e) => setNewNote({ ...newNote, is_internal: e.target.checked })}
                          />
                          Internal Note (not visible to applicant)
                        </label>
                        <button className="btn-add-note" onClick={handleAddNote}>
                          Add Note
                        </button>
                      </div>
                    </div>

                    <div className="notes-list">
                      <h3>Previous Notes</h3>
                      {selectedApp.notes.length === 0 ? (
                        <p className="no-data">No notes yet</p>
                      ) : (
                        selectedApp.notes.map((note) => (
                          <div key={note.id} className="note-item">
                            <div className="note-header">
                              <span className="note-author">{note.admin_name}</span>
                              <span className="note-date">{new Date(note.created_at).toLocaleString()}</span>
                              {note.is_internal && <span className="note-badge">Internal</span>}
                            </div>
                            <p className="note-content">{note.note}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {modalTab === 'history' && (
                  <div className="history-view">
                    <div className="timeline">
                      {selectedApp.statusHistory.map((history) => (
                        <div key={history.id} className="timeline-item">
                          <div className="timeline-marker" style={{ background: statusColor(history.new_status) }}></div>
                          <div className="timeline-content">
                            <div className="timeline-header">
                              <span className="status-change">
                                <span className="old-status">{statusLabel(history.old_status)}</span>
                                →
                                <span className="new-status" style={{ color: statusColor(history.new_status) }}>
                                  {statusLabel(history.new_status)}
                                </span>
                              </span>
                              <span className="timeline-date">{new Date(history.created_at).toLocaleString()}</span>
                            </div>
                            <div className="timeline-meta">
                              Changed by: {history.changed_by_name || 'System'}
                            </div>
                            {history.notes && (
                              <div className="timeline-notes">{history.notes}</div>
                            )}
                          </div>
                        </div>
                      ))}
                      {/* The submission itself is the first event of every application. */}
                      <div className="timeline-item">
                        <div className="timeline-marker" style={{ background: statusColor('pending') }}></div>
                        <div className="timeline-content">
                          <div className="timeline-header">
                            <span className="status-change">Application submitted</span>
                            <span className="timeline-date">{new Date(app.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </AdminLayout>
  );
};

export default ApplicationsManagement;
